/* ==========================================================================
   Bien — ambient layer (pointer field + viewport gating)
   --------------------------------------------------------------------------
   See the header of bien-ambient.css for what this layer is and the four
   performance rules it holds to. This file is responsible for two of them:

     (c) Nothing animates off screen. An IntersectionObserver toggles
         .amb-live on each chapter, and every animation in the stylesheet is
         gated behind that class. Five chapters exist; at most one or two are
         ever running.

     (d) No transitions on anything written per frame. The depth transform on
         each layer is written DIRECTLY onto the element, already smoothed by
         the lerp below. A CSS transition on the same property would restart
         sixty times a second and never settle.

   Cost profile, deliberately:

   * One rAF loop for the whole page, and it parks itself once the smoothed
     pointer has caught up and energy has decayed. An idle page runs nothing.
   * Per frame it writes transforms to the layers of VISIBLE chapters only —
     four elements each, a handful of chapters, all of them promoted with
     will-change so the write is a compositor operation and not a repaint.
   * Layout is read only when the page moves under us: scroll, resize, or an
     intersection change set a flag, and the next frame re-measures. Never
     per frame, and never inside the write loop.
   * This file never touches .chapter__bg. That element's transform is owned
     by each section's own scroll-parallax loop; cursor depth is applied to
     the layers INSIDE it so the two compose.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bienAmbient) return;
  window.__bienAmbient = true;

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- state */

  var targetX = 0, targetY = 0;   // where the pointer is
  var curX = 0, curY = 0;         // the smoothed value we publish
  var LERP = 0.085;               // ~200ms tail, so things trail with weight

  var energy = 0;
  var lastX = 0, lastY = 0;
  var DECAY = 0.94;

  var rawX = 0, rawY = 0;
  var hasPointer = false;
  var running = false;
  var stale = true;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* --------------------------------------------------- chapters + layers */

  // A chapter is only in `activeStacks` while it is on screen. Everything the
  // frame loop does is scoped to that list.
  var stacks = [];        // { el, layers:[{el, depth}], on:false }
  var activeStacks = [];

  var gate = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var s = entries[i].target.__ambStack;
      if (!s) continue;
      s.on = entries[i].isIntersecting;
      // The class is what un-pauses every animation in this chapter. It goes
      // on the CHAPTER, not the layer stack, so it also gates the details in
      // .chapter__content — the divider glints are siblings of the stack, not
      // descendants, and would otherwise run on all five chapters at once.
      s.host.classList.toggle('amb-live', s.on);
    }
    rebuild();
    stale = true;
    wake();
  }, { rootMargin: '15% 0px' });

  function rebuild() {
    activeStacks = [];
    for (var i = 0; i < stacks.length; i++) if (stacks[i].on) activeStacks.push(stacks[i]);
  }

  function collectStacks() {
    var found = document.querySelectorAll('.amb-stack');
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.__ambStack) continue;
      var layers = [];
      var kids = el.querySelectorAll('.amb-l');
      for (var k = 0; k < kids.length; k++) {
        layers.push({ el: kids[k], depth: parseFloat(kids[k].getAttribute('data-depth')) || 6 });
      }
      // The stack is always inserted as the first child of the element it
      // fills, so that parent is the right place for .amb-live: it contains
      // both the layers and the section's own content, which is what the
      // gated at-rest rules (divider glint, sheen) are written against.
      var s = { el: el, host: el.parentElement || el, layers: layers, on: false };
      el.__ambStack = s;
      stacks.push(s);
      gate.observe(el);
    }
  }

  /* ------------------------------------------------- the breathing (removed) */

  // There used to be a spawner here that bloomed a randomly placed light
  // somewhere in whichever section was on screen, roughly sixteen in the air
  // at a time, each one opening and closing over six seconds. It has been
  // removed: the lighting is now only the two resting glows each section
  // positions for itself via bien-ambient-stack's glow_a/glow_b, so a
  // section's light is deliberate and stays put instead of appearing at
  // random around the page. The .amb-glowfield container it spawned into is
  // still used by those two resting glows.

  /* ------------------------------------------------ pointer-field elements */

  // Too many of these to tag by hand, and tagging by selector means a media
  // slot added later picks the behaviour up for free.
  var AUTO_LOCAL = '.bien-story .ph, .site-header__link, .site-header__reserve';

  var locals = [];
  var activeLocals = [];

  var localGate = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i].target.__ambLocal;
      if (e) e.on = entries[i].isIntersecting;
    }
    rebuildLocals();
    stale = true;
    wake();
  }, { rootMargin: '20% 0px' });

  function rebuildLocals() {
    activeLocals = [];
    for (var i = 0; i < locals.length; i++) if (locals[i].on) activeLocals.push(locals[i]);
  }

  function collectLocals() {
    var found = document.querySelectorAll('[data-amb-local], ' + AUTO_LOCAL);
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.__ambLocal) continue;
      var e = { el: el, rect: null, on: false };
      el.__ambLocal = e;
      locals.push(e);
      localGate.observe(el);
    }
  }

  // --amb-energy is read by the divider diamonds and by anything carrying
  // .amb-lift — a handful of elements, all of them leaves. Writing to each of
  // them directly is free; writing once to :root is not. The value is only
  // pushed when it has moved enough to be visible, so a slow drift of the
  // pointer does not touch the DOM at all.
  var energyEls = [];
  var lastEnergy = -1;

  function collectEnergy() {
    var found = document.querySelectorAll('.divider__mark, .amb-lift');
    for (var i = 0; i < found.length; i++) {
      if (found[i].__ambEnergy) continue;
      found[i].__ambEnergy = true;
      energyEls.push(found[i]);
    }
  }

  function publishEnergy(force) {
    if (!force && Math.abs(energy - lastEnergy) < 0.02) return;
    lastEnergy = energy;
    var v = energy.toFixed(3);
    for (var i = 0; i < energyEls.length; i++) {
      energyEls[i].style.setProperty('--amb-energy', v);
    }
  }

  function measure() {
    for (var i = 0; i < activeLocals.length; i++) {
      activeLocals[i].rect = activeLocals[i].el.getBoundingClientRect();
    }
    stale = false;
  }

  /* ------------------------------------------------------------- the loop */

  function frame() {
    curX += (targetX - curX) * LERP;
    curY += (targetY - curY) * LERP;
    energy *= DECAY;

    // NOTE: nothing here writes a custom property to :root, and that is the
    // single most important line in this file.
    //
    // A custom property set on the document element invalidates style for the
    // WHOLE document, because any descendant could inherit it. Measured on the
    // live storefront (about 2,000 nodes): one :root custom-property write
    // costs ~9ms of style recalc. At sixty writes a second that is the entire
    // frame budget, and it is what made both earlier versions of this layer
    // slow. The same write to a leaf element costs 0ms, because the
    // invalidation is scoped to that element's subtree.
    //
    // So: --amb-mx/--amb-my are never published at all (the depth transforms
    // below are written straight onto the layers), and --amb-energy goes only
    // to the handful of elements that actually read it.
    publishEnergy();

    if (stale) measure();

    // Depth. Written straight onto the element — already smoothed, so there
    // is nothing for a CSS transition to add except a restart every frame.
    for (var i = 0; i < activeStacks.length; i++) {
      var layers = activeStacks[i].layers;
      for (var j = 0; j < layers.length; j++) {
        var d = layers[j].depth;
        layers[j].el.style.transform =
          'translate3d(' + (curX * d).toFixed(2) + 'px,' + (curY * d * 0.55).toFixed(2) + 'px,0)';
      }
    }

    for (var n = 0; n < activeLocals.length; n++) {
      var e = activeLocals[n];
      var r = e.rect;
      if (!r || !r.width || !r.height) continue;
      var hw = r.width / 2, hh = r.height / 2;
      var cx = r.left + hw, cy = r.top + hh;
      var dx = Math.abs(rawX - cx) / hw;
      var dy = Math.abs(rawY - cy) / hh;
      var near = hasPointer ? clamp(1 - (Math.max(dx, dy) - 1) / 0.85, 0, 1) : 0;
      var st = e.el.style;
      st.setProperty('--amb-near', near.toFixed(3));
      st.setProperty('--amb-lx', clamp((rawX - cx) / hw, -1, 1).toFixed(3));
      st.setProperty('--amb-ly', clamp((rawY - cy) / hh, -1, 1).toFixed(3));
      st.setProperty('--amb-fx', (clamp((rawX - r.left) / r.width, 0, 1) * 100).toFixed(1) + '%');
      st.setProperty('--amb-fy', (clamp((rawY - r.top) / r.height, 0, 1) * 100).toFixed(1) + '%');
    }

    // Park once the pointer has caught up and energy has bottomed out.
    if (Math.abs(targetX - curX) < 0.0008 &&
        Math.abs(targetY - curY) < 0.0008 &&
        energy < 0.002) {
      running = false;
      energy = 0;
      publishEnergy(true);
    } else {
      requestAnimationFrame(frame);
    }
  }

  function wake() {
    if (running || reduced) return;
    running = true;
    requestAnimationFrame(frame);
  }

  /* ---------------------------------------------- scroll-driven keyframes */

  // THE BACKGROUND LAYER IS GONE, on purpose.
  //
  // What used to be built here: one full-document .amb-page layer carrying the
  // guilloche, three rayon zones, the film grain, plus two fixed margin
  // power-reserve gauges and the pin-freeze/parallax machinery that held all of
  // it still against the four pinned sections. Every section is now simply the
  // page colour with nothing painted behind it.
  //
  // The pointer field above stays: sections read --amb-fx/--amb-fy/--amb-near
  // and --amb-energy from it, and .amb-live is what un-pauses each chapter's
  // own at-rest details. Only the drawing was removed.

  function maxScroll() {
    return Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  }

  var SCROLL_TIMELINE = CSS.supports('animation-timeline', 'scroll(root block)');
  var pinStyleEl = null;

  // The one keyframe set left: a layout fact the stylesheet cannot know, which
  // is where the marquee runway falls in the scroll range.
  function writeScrollKeyframes() {
    if (!SCROLL_TIMELINE) return;
    if (!pinStyleEl) {
      pinStyleEl = document.createElement('style');
      document.head.appendChild(pinStyleEl);
    }
    pinStyleEl.textContent = floatKeyframes();
  }

  // The marquee frame drifts against the page across its own runway, so it
  // reads as a card held in front of the texture. Outside that span the
  // keyframes are clamped by fill-mode, which only ever applies off screen.
  var FLOAT_TRAVEL = 22;

  function floatKeyframes() {
    if (reduced) return '';
    var el = document.querySelector('.marquee-runway');
    if (!el) return '';
    var max = maxScroll();
    var a = ((el.getBoundingClientRect().top + window.scrollY - window.innerHeight) / max) * 100;
    var b = ((el.getBoundingClientRect().top + window.scrollY + el.offsetHeight) / max) * 100;
    a = Math.max(0, Math.min(100, a));
    b = Math.max(0, Math.min(100, b));
    if (b - a < 0.01) return '';
    return '@keyframes amb-marquee-float{' +
      a.toFixed(4) + '%{translate:0 ' + FLOAT_TRAVEL + 'px}' +
      b.toFixed(4) + '%{translate:0 ' + (-FLOAT_TRAVEL) + 'px}}';
  }

  /* ---------------------------------------------- split-type sheen index */

  // The story sections split display type into per-character spans on reveal.
  // Tag each with its index so the sheen can stagger along the word.
  function indexSheen() {
    var hosts = document.querySelectorAll('[data-amb-sheen]');
    for (var i = 0; i < hosts.length; i++) {
      var chars = hosts[i].querySelectorAll('.split-char');
      if (!chars.length || hosts[i].__n === chars.length) continue;
      hosts[i].__n = chars.length;
      for (var n = 0; n < chars.length; n++) chars[n].style.setProperty('--amb-i', n);
    }
  }

  /* ---------------------------------------------------------------- input */

  function onMove(e) {
    hasPointer = true;
    rawX = e.clientX;
    rawY = e.clientY;
    var nx = clamp((rawX / window.innerWidth - 0.5) * 2, -1, 1);
    var ny = clamp((rawY / window.innerHeight - 0.5) * 2, -1, 1);
    var vx = nx - lastX, vy = ny - lastY;
    lastX = nx; lastY = ny;
    energy = Math.min(1, energy + Math.sqrt(vx * vx + vy * vy) * 5.5);
    targetX = nx;
    targetY = ny;
    wake();
  }

  function onLeave() {
    hasPointer = false;
    targetX = 0;
    targetY = 0;
    wake();
  }

  /* ----------------------------------------------------------------- boot */

  function scan() { collectStacks(); collectLocals(); collectEnergy(); indexSheen(); }


  function boot() {
    scan();

    if (reduced) {
      // Publish the resting frame once, mark every chapter live so the static
      // layers are visible, and stop. Nothing animates: the stylesheet's
      // reduced-motion block cancels each animation individually.
      publishEnergy(true);
      for (var i = 0; i < stacks.length; i++) stacks[i].host.classList.add('amb-live');
      return;
    }

    writeScrollKeyframes();

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave, { passive: true });
    window.addEventListener('blur', onLeave);
    window.addEventListener('scroll', function () { stale = true; wake(); }, { passive: true });
    window.addEventListener('resize', function () { stale = true; writeScrollKeyframes(); wake(); }, { passive: true });

    // The document grows as images and fonts settle, which moves the marquee
    // runway's place in the scroll range. Fires on layout changes only.
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { writeScrollKeyframes(); });
      ro.observe(document.body);
    }

    // The split scripts belong to other sections and land after us.
    setTimeout(function () { scan(); indexSheen(); writeScrollKeyframes(); }, 600);
    setTimeout(function () { scan(); indexSheen(); writeScrollKeyframes(); }, 1800);

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () { scan(); writeScrollKeyframes(); wake(); }, 60);
    });

    wake();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
